import { AlertTriangle, Building2, Calendar, ListTodo, MapPin } from 'lucide-react';
import React from 'react';
import { useGetTopProjectsQuery, useGetProjectOverviewQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const ProjectsListWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  // For org scope, fetch top projects; for project scope, fetch single project
  const {
    data: topProjects,
    isLoading: isTopProjectsLoading,
    error: topProjectsError,
  } = useGetTopProjectsQuery(
    { limit: size === '2x2' ? 6 : 3 },
    { skip: scope !== 'org', pollingInterval: 300000 }
  );

  const {
    data: projectOverview,
    isLoading: isProjectLoading,
    error: projectError,
  } = useGetProjectOverviewQuery(
    { projectId: projectId || '' },
    { skip: scope !== 'project' || !projectId, pollingInterval: 300000 }
  );

  const isLoading = scope === 'org' ? isTopProjectsLoading : isProjectLoading;
  const error = scope === 'org' ? topProjectsError : projectError;

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={3} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load projects</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  // Build projects array based on scope
  const projects =
    scope === 'org'
      ? topProjects || []
      : projectOverview
        ? [
            {
              id: projectId || '',
              name: projectOverview.projectName || 'Current Project',
              status: projectOverview.projectStatus || 'active',
              location: 'Project Location',
              dueDate: projectOverview.endDate || null,
              progress: projectOverview.completionPercentage || 0,
            },
          ]
        : [];

  if (projects.length === 0) {
    return (
      <WidgetEmptyState
        title="No Projects"
        description="No projects have been created yet"
        icon={ListTodo}
      />
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700';
      case 'planning':
        return 'bg-yellow-100 text-yellow-700';
      case 'completed':
        return 'bg-blue-100 text-blue-700';
      case 'on_hold':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress >= 80) return 'bg-green-500';
    if (progress >= 50) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const displayLimit = size === '2x2' ? 6 : 3;

  return (
    <div className="space-y-3">
      {projects.slice(0, displayLimit).map(project => (
        <div
          key={project.id}
          className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-start space-x-2 flex-1">
              <Building2 className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <h4 className="text-body-sm font-medium text-gray-900 truncate">{project.name}</h4>
                <div className="flex items-center space-x-3 mt-1">
                  <div className="flex items-center space-x-1">
                    <MapPin className="h-3 w-3 text-gray-400" />
                    <span className="text-meta text-gray-500">{project.location}</span>
                  </div>
                  {project.dueDate && (
                    <div className="flex items-center space-x-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <span className="text-meta text-gray-500">
                        {new Date(project.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <span className={`text-meta px-2 py-1 rounded-full ${getStatusColor(project.status)}`}>
              {formatStatus(project.status)}
            </span>
          </div>

          {/* Progress Bar */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <span className="text-meta text-gray-500">Progress</span>
              <span className="text-meta font-medium text-gray-900">{project.progress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-1.5">
              <div
                className={`h-1.5 rounded-full transition-all duration-300 ${getProgressColor(project.progress)}`}
                style={{ width: `${project.progress}%` }}
              />
            </div>
          </div>
        </div>
      ))}

      {scope === 'org' && projects.length > displayLimit && (
        <div className="pt-2 border-t border-gray-100">
          <p className="text-meta text-gray-500 text-center">
            +{projects.length - displayLimit} more projects
          </p>
        </div>
      )}
    </div>
  );
};

export default ProjectsListWidget;
